import { useEffect, useState } from "preact/hooks";
import { FindForm } from "../components/FindForm";
import { Monogram } from "../components/Monogram";
import { Rule } from "../components/Rule";
import { Rsvp } from "./Rsvp";
import { fetchOwnInvite, type Invite } from "../lib/api";
import { rsvp as copy } from "../content/wedding";

/**
 * `/rsvp`: the name box, and the form behind it.
 *
 * This is the whole of how a guest reaches their invitation. There is no
 * bespoke link and no printed code — a full name and the postcode we posted
 * the invitation to, and nothing else opens anything.
 *
 * The box itself is `FindForm`, shared with the dialog that guards the room
 * block's booking link. Everything about why it is unhelpful to anyone it is
 * not meant for — one sentence for every way of failing, a cookie rather than
 * anything that names the party, no field ever reported on alone — lives there
 * and in `apps/api/src/party.ts`. What matters here is not to undo it by being
 * friendlier than the Worker is.
 */

/** Whether we already know who this is: probing, no, or yes. */
type Phase = "probing" | "asking" | "found";

export function RsvpFind() {
  const [phase, setPhase] = useState<Phase>("probing");
  const [invite, setInvite] = useState<Invite | null>(null);
  /**
   * Only whether the box is refusing, not what it said — the Worker's sentence
   * is already on screen, and this decides whether the aside underneath
   * repeats its closing advice or gets out of the way.
   */
  const [failed, setFailed] = useState(false);

  /**
   * A pass from earlier — a reload, or coming back to amend a reply — means
   * the form can open straight away rather than asking for the name twice.
   */
  useEffect(() => {
    const controller = new AbortController();
    fetchOwnInvite(controller.signal)
      .then((next) => {
        setInvite(next);
        setPhase("found");
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError")
          return;
        // Anything at all, including the server being down: ask for the name.
        setPhase("asking");
      });
    return () => controller.abort();
  }, []);

  /** Back to the box, from an expired pass or a "not you?" on a shared phone. */
  function reset() {
    setInvite(null);
    setFailed(false);
    setPhase("asking");
  }

  if (phase === "probing") {
    return (
      <main class="rsvp rsvp--centred">
        <div class="shell notice" aria-live="polite">
          <Monogram class="notice__mark" />
          <p class="notice__wait">One moment…</p>
        </div>
      </main>
    );
  }

  // `initial` only when the probe already fetched it; after a lookup the form
  // fetches for itself, which is one request either way.
  if (phase === "found") {
    return <Rsvp initial={invite ?? undefined} onExpired={reset} />;
  }

  return (
    <main class="rsvp rsvp--centred">
      <div class="shell notice notice--find">
        <Monogram class="notice__mark" />
        <h1>Find your invitation</h1>
        <p>
          Your name as we wrote it on the envelope, and the postcode we sent it
          to. That opens the form already knowing who is on your invitation, so
          everyone on it can reply together.
        </p>

        <Rule class="find__rule" style={{ marginInline: "auto" }} />

        <FindForm
          onFound={() => {
            // The pass is set; let the form fetch the invitation behind it.
            setInvite(null);
            setPhase("found");
          }}
          onFailureChange={setFailed}
        />

        {/* The Worker's refusal already says to write to us, word for word.
            Saying it again directly underneath reads as a form that has run
            out of ideas, so while one is on screen this shrinks to the part
            it does not repeat. */}
        <p class="notice__aside">
          {failed ? (
            <>Replies {copy.deadline}.</>
          ) : (
            <>
              Still nothing? Send either of us a message and we will sort it
              out. Replies {copy.deadline}.
            </>
          )}
        </p>
        <a class="notice__back find__home" href="/">
          Back to the beginning
        </a>
      </div>
    </main>
  );
}
